import GigOffer from './GigOffer'
import cloneDeep from 'lodash/cloneDeep'
import maxBy from 'lodash/maxBy'
import unescape from 'lodash/unescape'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { GigApplicationStatuses } from '@/constants/gig/gigApplicationStatuses'
import { DEFAULT_GIG_APPLICATION_MSG } from '@/constants/gig/gigMessages'
import { GigTimeTypes } from '@/constants/gig/gigTimeTypes'
import AccountType from '@/constants/user/AccountType'
import { CustomerTypes } from '@/constants/user/roles'
import Avatar from '@/models-ts/user/Avatar'
import { parseSlug } from '@/utils-ts/parser'
import { getDateFromString } from '@/utils/date'

export default class MyGigApplication {
  id: number
  gigId: number
  deadline?: number
  comment?: string
  budget?: string
  status: number
  moderationStage: ModerationStages
  createdAt: Date
  customer: {
    id: number
    name: string
    avatar: Avatar
    type: AccountType
    reviewsCount: number
    avgReviews: string
    is_customer_verified: boolean
    customerType: CustomerTypes
  } | null
  freelancer: {
    id: number
    name: string
    avatar: Avatar
    type: AccountType
    reviewsCount: number
    avgReviews: string
  } | null
  gig: {
    id: number
    slug: string
    name: string
    timeType: GigTimeTypes
  }
  offer?: GigOffer | null

  constructor(data: Partial<MyGigApplication>) {
    Object.assign(this, cloneDeep(data))
  }

  static fromServer(data: LX.MyGigApplication) {
    const offer = maxBy(
      (data.relations?.Offers || []).map((offer) => ({
        ...offer,
        createdDate: +getDateFromString(offer.created_at),
      })),
      'createdDate'
    )
    const comment = data.comment === DEFAULT_GIG_APPLICATION_MSG ? '' : data.comment

    let customer = null
    if (data.relations?.Customer) {
      customer = {
        id: data.relations.Customer.id,
        name: data.relations.Customer.name,
        avatar: Avatar.fromServer(data.relations.Customer.avatar),
        type: data.relations.Customer.type,
        avgReviews: (data.relations.Customer.profile.avg_reviews || 0).toFixed(2),
        reviewsCount: data.relations.Customer.reviews_count,
        is_customer_verified: Number(data.relations?.Customer?.is_customer_verified) === 1,
        customerType: data.relations?.Customer?.profile?.type,
      }
    }

    let freelancer = null
    if (data.relations?.Freelancer) {
      freelancer = {
        id: data.relations.Freelancer.id,
        name: data.relations.Freelancer.name,
        avatar: Avatar.fromServer(data.relations.Freelancer.avatar),
        type: data.relations.Freelancer.type,
        avgReviews: (data.relations.Freelancer.profile.avg_reviews || 0).toFixed(2),
        reviewsCount: data.relations.Freelancer.reviews_count,
      }
    }

    return new MyGigApplication({
      id: data.id,
      status: data.status,
      moderationStage: data.moderation_stage || ModerationStages.PASSED,
      gigId: data.gig_id,
      comment,
      deadline: data.deadline,
      budget: data.budget?.amount,
      createdAt: getDateFromString(data.created_at),
      offer: offer ? GigOffer.fromServer(offer) : null,
      customer,
      freelancer,
      gig: {
        id: data.relations?.Gig.id || 0,
        slug: parseSlug(data.relations?.Gig?.slug),
        name: unescape(data.relations?.Gig?.name),
        timeType: Number(data.relations?.Gig?.time_type) || GigTimeTypes.FIXED,
      },
    })
  }

  get sortField() {
    return this.createdAt
  }

  get deadlineInDays() {
    return (this.deadline || 0) / 86400
  }
}
